import { f0FormField } from '@factorialco/f0-react'
import type { F0SelectItemProps } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import type { PayrollConceptWithCategory } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { getFirstPaymentOnValidationError } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/lib/getFirstPaymentOnValidationError'

import {
  FirstPaymentInfoCallout,
  PaymentFrequencyHeader,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/AdditionalCompensationWizardFields'

import {
  type AdditionalCompensationWizardAmountStrategyType,
  AMOUNT_STRATEGY_TYPE_VALUES,
  PER_WORKED_DAY_STRATEGY,
  RECURRENCE_VALUES,
} from './additionalCompensationWizard.shared'
import { shouldShowCompensationExcludedFromFirstPeriodCallout } from './additionalCompensationWizard.utils'

const buildBasicSchema = ({
  payrollConceptOptions,
  amountStrategyTypeOptions,
  recurrenceOptions,
  effectiveOn,
  defaultFirstPaymentDate,
}: {
  payrollConceptOptions: PayrollConceptWithCategory[]
  amountStrategyTypeOptions: Array<
    F0SelectItemProps<AdditionalCompensationWizardAmountStrategyType>
  >
  recurrenceOptions: Array<F0SelectItemProps<(typeof RECURRENCE_VALUES)[number]>>
  effectiveOn?: string
  defaultFirstPaymentDate?: Date
}) => {
  const amountStrategyTypeSchema = z.enum(AMOUNT_STRATEGY_TYPE_VALUES)

  return z.object({
    payrollConceptId: f0FormField(z.string().min(1), {
      label: i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.label'
      ),
      placeholder: i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.payroll_concept_select_field.placeholder'
      ),
      showSearchBox: true,
      options: payrollConceptOptions,
    }),
    amountStrategyType: f0FormField(amountStrategyTypeSchema, {
      label: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.label'
      ),
      placeholder: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.placeholder'
      ),
      options: amountStrategyTypeOptions,
    }),
    paymentFrequencyHeader: f0FormField(z.string().optional(), {
      label: '',
      fieldType: 'custom',
      render: () => <PaymentFrequencyHeader />,
    }),
    recurrence: f0FormField(z.enum(RECURRENCE_VALUES), {
      label: i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.label'
      ),
      placeholder: i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.recurrence.placeholder'
      ),
      options: recurrenceOptions,
      disabled: ({ values }: { values: Record<string, unknown> }) =>
        values.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      resetOnDisable: true,
    }),
    firstPaymentOn: f0FormField(
      z.date().superRefine((value, context) => {
        const validationError = getFirstPaymentOnValidationError({
          firstPaymentOn: value,
          effectiveOn,
          minFirstPaymentOn: defaultFirstPaymentDate,
        })

        if (validationError) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: validationError,
          })
        }
      }),
      {
        label: i18n.t(
          'contract_versions.additional_compensation.additional_compensation_form_fields.firstPaymentOn.label'
        ),
        fieldType: 'date',
        granularities: ['month'],
      }
    ),
    firstPaymentOnInfoCallout: f0FormField(z.string().optional(), {
      label: '',
      fieldType: 'custom',
      renderIf: ({ values }: { values: Record<string, unknown> }) =>
        shouldShowCompensationExcludedFromFirstPeriodCallout({
          firstPaymentOn: values.firstPaymentOn,
          effectiveOn,
        }),
      render: () => <FirstPaymentInfoCallout />,
    }),
  })
}

export { buildBasicSchema }
